import { infiniteSearchForListingsQueryOptions } from "@/api/listingSearch";
import { resourcesHomeQuery } from "@/api/resources";
import { queryClient } from "@/api/utils";
import { OfferCard } from "@/components/OfferCard";
import { PageInset } from "@/components/PageInset";
import { ResourceCard } from "@/components/ResourceCard";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { Shelf } from "@/components/ui/Shelf";
import { TopAppBar } from "@/components/ui/TopAppBar";
import { seo } from "@/utils/seo";
import { Box } from "@givenwell/components";
import { ResourceItem, SearchResult } from "@givenwell/marketplace-api";
import { useInfiniteQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { fallback, object, optional, string } from "valibot";
import { IntroCard } from "./IntroCard";

export const Route = createFileRoute("/_auth/_app/resources/")({
  validateSearch: object({
    category: fallback(optional(string()), undefined),
  }),
  loader: () => {
    queryClient.prefetchQuery(resourcesHomeQuery);
  },
  component: ResourcesPage,
  head: () => ({ meta: seo({ title: "Resources" }) }),
});

const route = getRouteApi("/_auth/_app/resources/");

function ResourcesPage() {
  const { data: themes } = useSuspenseQuery(resourcesHomeQuery);
  const categories = themes.flatMap(theme => {
    const categoryId = theme.resources?.[0]?.category_id;
    if (!categoryId) return [];
    return [
      {
        name: theme.name || "",
        id: categoryId,
      },
    ];
  });

  const search = route.useSearch();

  const isSearching = !!search.category;

  const { data: offersPages } = useInfiniteQuery(
    infiniteSearchForListingsQueryOptions({
      search_text: null,
      category_id: [],
      variant_types: ["offer"],
    }),
  );

  const offers = offersPages?.pages.flatMap(p => p.items);

  return (
    <PageWrapper topAppBar={<TopAppBar />}>
      <PageInset>
        <Box css={{ width: "100%", maxW: 2000, mx: "auto" }}>
          <IntroCard />
          <Box css={{ pt: "var(--layout-margin)" }} />
          {offers && offers.length > 0 && <OffersShelf offers={offers} />}
          {themes.map((list, i) => (
            <ResourceList key={i} name={list.name || ""} resources={list.resources || []} />
          ))}
        </Box>
      </PageInset>
    </PageWrapper>
  );
}

function OffersShelf({ offers }: { offers: SearchResult[] }) {
  return (
    <Shelf title="Givenwell exclusive offers" size="big">
      {offers.map(offer => (
        <OfferCard key={offer.listing_id} listing={offer} />
      ))}
    </Shelf>
  );
}

function ResourceList({ name, resources }: { name: string; resources: ResourceItem[] }) {
  return (
    <Shelf
      title={name}
      enableSeeMore
      seeMoreLinkOptions={{
        to: "/resources",
        search: {
          category: resources[0]?.category_id,
        },
      }}
    >
      {resources.map(resource => (
        <ResourceCard key={resource.resource_id} resource={resource} />
      ))}
    </Shelf>
  );
}
